/**
 * Composant de sélection du profil de disponibilité
 * 
 * Permet au joueur de choisir son niveau de disponibilité
 * pour les invitations à jouer (Bougon -> On fire!).
 */

import { useState, useEffect, useCallback } from 'react';
import type { AvailabilityProfile as AvailabilityProfileType } from '../types/draft';
import { AVAILABILITY_LABELS } from '../types/draft';
import { BACKEND_URL } from '../config';

const PROFILES: AvailabilityProfileType[] = [
  'bougon', 'timide', 'detendu', 'enthousiaste', 'on_fire',
];

interface AvailabilityProfileProps {
  pseudo: string;
  token?: string;
}

export default function AvailabilityProfile({ pseudo, token }: AvailabilityProfileProps) {
  const [profile, setProfile] = useState<AvailabilityProfileType>('detendu');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : undefined;

  // Charger le profil
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/profile/availability?pseudo=${encodeURIComponent(pseudo)}`,
          { headers: authHeaders },
        );
        if (response.ok) {
          const data = await response.json();
          setProfile(data.availability || 'detendu');
        }
      } catch (err) {
        console.error('[AvailabilityProfile] Load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pseudo, token]);

  // Sauvegarder le profil
  const handleSelect = useCallback(async (newProfile: AvailabilityProfileType) => {
    if (!token) return;

    setProfile(newProfile);
    setSaving(true);

    try {
      await fetch(`${BACKEND_URL}/api/profile/availability`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          pseudo,
          availability: newProfile,
        }),
      });
    } catch (err) {
      console.error('[AvailabilityProfile] Save error:', err);
    } finally {
      setSaving(false);
    }
  }, [pseudo, token]);

  if (loading) {
    return <div className="availability-loading">Chargement...</div>;
  }

  if (!token) {
    return <div className="availability-loading">Session requise pour modifier la disponibilité.</div>;
  }

  return (
    <div className="availability-profile">
      <div className="availability-slider">
        {PROFILES.map((p, index) => {
          const info = AVAILABILITY_LABELS[p];
          const isActive = p === profile;
          const activeIndex = PROFILES.indexOf(profile);

          return (
            <button
              key={p}
              className={`availability-option ${isActive ? 'active' : ''} ${index <= activeIndex ? 'filled' : ''}`}
              onClick={() => handleSelect(p)}
              disabled={saving}
              title={info.description}
            >
              <span className="availability-emoji">{info.emoji}</span>
              <span className="availability-label">{info.label}</span>
            </button>
          );
        })}
      </div>
      <div className="availability-description">
        <span className="availability-current-emoji">
          {AVAILABILITY_LABELS[profile].emoji}
        </span>
        <span className="availability-current-text">
          {AVAILABILITY_LABELS[profile].description}
        </span>
      </div>
    </div>
  );
}
